import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

import url from '../../constants/config';
import notFound from '../../assets/notFound.png';
import trackEvent from '../../shared/utils/eventTracking';
import Loader from '../../shared/displayComponents/loader/Loader';

import './RecommendationsPage.scss';

const getPlayerPhoto = imageBase64 => imageBase64 || notFound;

const RecommendationsPage = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const history = useHistory();

  useEffect(() => {
    let cancelled = false;

    const fetchRecommendations = async () => {
      try {
        const token = window.localStorage.getItem('authorization');
        if (!token) {
          setIsLoading(false);
          return;
        }
        const { data } = await axios.get(`${url}/coach/recommendations?limit=6`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setItems(data.recommendations || []);
        }
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchRecommendations();
    return () => { cancelled = true; };
  }, []);

  const handlePlayerClick = (playerId) => {
    trackEvent('recommendation_clicked', { playerId });
    history.push({
      pathname: `/available-players/${playerId}`,
      state: { source: 'recommendations' },
    });
  };

  if (isLoading) {
    return (
      <div className="rec-page">
        <div className="rec-page__header">
          <h2 className="rec-page__title">
            <i className="fas fa-magic" /> Recommended for You
          </h2>
          <p className="rec-page__subtitle">
            Finding your best matches...
          </p>
        </div>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rec-page">
        <div className="rec-page__header">
          <h2 className="rec-page__title">
            <i className="fas fa-magic" /> Recommended for You
          </h2>
        </div>
        <div className="rec-page__empty">
          <i className="fas fa-exclamation-circle" />
          <h3>Something went wrong</h3>
          <p>We couldn't load recommendations right now. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rec-page">
        <div className="rec-page__header">
          <h2 className="rec-page__title">
            <i className="fas fa-magic" /> Recommended for You
          </h2>
        </div>
        <div className="rec-page__empty">
          <div className="rec-page__empty-icon">
            <i className="fas fa-search" />
          </div>
          <h3>No recommendations yet</h3>
          <p>
            Browse and search players to get personalized recommendations.
            The more you use the platform, the better our suggestions get!
          </p>
          <button
            type="button"
            className="rec-page__cta"
            onClick={() => history.push('/available-players')}
          >
            <i className="fas fa-search" /> Browse Players
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rec-page">
      <div className="rec-page__header">
        <h2 className="rec-page__title">
          <i className="fas fa-magic" /> Recommended for You
        </h2>
        <p className="rec-page__subtitle">
          Personalized picks based on your searches, favorites, and recruiting preferences.
          The more you use PlaySport, the smarter these suggestions become.
        </p>
      </div>

      <div className="rec-page__grid">
        {items.map(({ player }) => {
          const playerId = player._id;

          return (
            <div
              key={playerId}
              className="rec-card"
              onClick={() => handlePlayerClick(playerId)}
            >
              {/* Image */}
              <div className="rec-card__image-container">
                <img
                  src={getPlayerPhoto(player.imageBase64)}
                  alt={`${player.firstName} ${player.lastName}`}
                  className="rec-card__image"
                />
                {player.transfer && (
                  <span className="rec-card__transfer">Transfer</span>
                )}
              </div>

              {/* Body */}
              <div className="rec-card__body">
                <h3 className="rec-card__name">
                  {player.firstName} {player.lastName}
                </h3>

                <div className="rec-card__stats">
                  <div className="rec-card__stat">
                    <span className="rec-card__stat-label">Sport</span>
                    <span className="rec-card__stat-value">{player.sport || '-'}</span>
                  </div>
                  <div className="rec-card__stat">
                    <span className="rec-card__stat-label">Available</span>
                    <span className="rec-card__stat-value">{player.graduationYear || '-'}</span>
                  </div>
                </div>

                {!!player.position?.length && (
                  <div className="rec-card__info">
                    <i className="fas fa-volleyball-ball" />
                    <span>{player.position.join(', ')}</span>
                  </div>
                )}

                {player.height && (
                  <div className="rec-card__info">
                    <i className="fas fa-ruler-vertical" />
                    <span>{player.height}</span>
                  </div>
                )}

                <button
                  type="button"
                  className="rec-card__btn"
                  onClick={() => handlePlayerClick(playerId)}
                >
                  View Profile
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecommendationsPage;
